<template>
  <ContentSection spacing="lg" :class="{ 'pt-0 sm:pt-0': index === 0 }">
    <Container use-full-width>
      <!-- TITLE -->
      <h2 class="font-semibold typo-h1-sm">
        {{ category.translated.name }}
      </h2>

      <div v-if="categoriesLoaded">
        <!-- FILTER -->
        <ProductFilter
          v-model="criteria"
          class="mb-12 mt-9"
          :available-filters="availableFilters"
          :categories="categories"
          :sub-categories="subCategories"
        />

        <!-- TOTAL -->
        <div class="mb-18 flex h-8 items-center text-grey-400 typo-body">
          <span v-show="total > 0">{{ total }} {{ $t('products_found', total) }}</span>
        </div>
        <!-- PRODUCTS -->
        <TransitionGroup
          tag="ul"
          name="list"
          class="grid grid-cols-[repeat(auto-fill,_minmax(20.5rem,_1fr))] gap-x-default gap-y-default"
        >
          <li v-for="product in products" :key="product.id" class="flex">
            <CardProduct :data="product" />
          </li>
        </TransitionGroup>

        <!-- LOAD MORE TRIGGER -->
        <div
          v-show="page < pageCount"
          ref="loadMoreTrigger"
          class="mx-auto mt-24 w-fit rounded bg-primary-900 p-3"
        >
          <div class="flex items-center justify-center gap-1">
            <div class="size-[0.4375rem] animate-bounce rounded-full bg-white" />
            <div class="size-[0.4375rem] animate-bounce rounded-full bg-white [animation-delay:0.25s]" />
            <div class="size-[0.4375rem] animate-bounce rounded-full bg-white [animation-delay:0.5s]" />
          </div>
        <!-- <IconLoad class="w-7" /> -->
        </div>

        <!-- NO RESULTS -->
        <Transition name="fade-in">
          <NoEntriesFound v-if="total === 0" />
        </Transition>
      </div>
      <div v-else class="mt-9 flex h-64 w-full items-center justify-center">
        <!-- Spinner Icon -->
        <Transition name="fade">
          <IconSpin class="size-12 animate-spin text-primary-900" />
        </Transition>
      </div>
    </Container>
  </Contentsection>
</template>

<script lang="ts" setup>
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import type { Schemas } from '#shopware'

gsap.registerPlugin(ScrollTrigger)

const props = defineProps({
  index: {
    type: Number,
    default: 0,
  },
  category: {
    type: Object,
    default: () => ({}),
  },
})

let scrollTrigger: ScrollTrigger | null = null
const loadMoreTrigger = ref<HTMLElement | null>(null)

const categories = computed(() => {
  if (!subCategoriesFlat || !Array.isArray(subCategoriesFlat.value)) {
    return []
  }

  return subCategoriesFlat.value
})

const subCategories = computed(() => {
  if (!subSubCategoriesFlat || !Array.isArray(subSubCategoriesFlat.value)) {
    return []
  }

  const categoryFilterId = (criteria.value.filter as { value: string }[])[0]?.value

  return subSubCategoriesFlat.value.filter(category => category.parentId === categoryFilterId)
})

const products = ref<Schemas['Product'][]>([])
const { getPaginatorOptions: productPaginator } = useProducts()
const criteria = ref<Schemas['ProductListingCriteria']>({
  properties: '',
  filter: [],
})

const {
  elements: fetchedProducts,
  // fetchStatus,
  availableFilters,
  // filterOptions,
  page,
  pageCount,
  nextPage,
  // sortingOptions,
  suspense,
  total,
  // isLoading,
  refetch,
} = usePaginatedListing(p => productPaginator(p, props.category.id, criteria, 16))
await suspense()

products.value = fetchedProducts.value

async function loadMoreProducts() {
  if (page.value >= pageCount.value) {
    return
  }

  nextPage()
  await refetch()

  products.value = [
    ...products.value,
    ...fetchedProducts.value,
  ]

  refreshScrollTrigger()
}

const { subCategoriesFlat, fetchSubCategories, subCategoriesLoading } = useSubCategories()
fetchSubCategories(props.category.id)
const { subCategoriesFlat: subSubCategoriesFlat, fetchSubCategories: fetchSubSubCategories } = useSubCategories()

const categoriesLoaded = ref(false)
watch(subCategoriesLoading, (newValue) => {
  if (!categoriesLoaded.value) {
    categoriesLoaded.value = !newValue
  }
})

watch(criteria, async () => {
  const categoryFilter = criteria.value.filter?.find((f) => {
    if (!('field' in f)) return
    if (f.type !== 'equalsAny') return
    return f.field === 'categoryTree'
  }) as { value: string }
  if (categoryFilter) {
    if (categoryFilter.value.split('|').length > 1) return

    await fetchSubSubCategories(categoryFilter.value)
  }

  page.value = 1
  await refetch()
  products.value = [...fetchedProducts.value]
  refreshScrollTrigger()
}, { deep: true })

async function refreshScrollTrigger() {
  // Refresh scroll trigger to reset the trigger position
  await nextTick()

  if (scrollTrigger) {
    scrollTrigger.refresh()
  }
}

onMounted(() => {
  // Scroll Trigger
  scrollTrigger = ScrollTrigger.create({
    trigger: loadMoreTrigger.value,
    start: 'top bottom', // Fires when the top of the trigger hits the bottom of the viewport
    end: 'bottom bottom', // Fires when the bottom of the trigger hits the bottom of the viewport
    onEnter: () => loadMoreProducts(),
  })
})

onUnmounted(() => {
  if (scrollTrigger) {
    scrollTrigger.kill()
  }
})
</script>
